import type { RunMove } from '../sim/running';
import type { Tunables } from '../sim/tunables';
import { el } from './dom';

/**
 * Catch timing ring (GDD 6.5, reference 04): a ring around the target shrinks to the inner ring at arrival.
 * It reads WAIT until a tap would count, then TAP!. Tap anywhere while it shows. The catch chance beside it
 * jumps when the tap is graded, with a chip showing what the tap was worth.
 */
export class CatchRing {
  readonly root = el('div', 'catchring');
  private readonly outer = el('div', 'cr-outer');
  private readonly inner = el('div', 'cr-inner');
  private readonly label = el('div', 'cr-label', 'WAIT');
  private readonly chance = el('div', 'cr-chance');
  private readonly chanceNum = el('b');
  private readonly chip = el('span', 'cr-chip');
  private readonly layer = el('div', 'tap-layer');
  private onTap: (() => void) | null = null;
  private hot = false;

  constructor() {
    this.chance.append('CATCH', this.chanceNum, this.chip);
    this.root.append(this.outer, this.inner, this.label, this.chance);
    this.root.hidden = true;
    this.layer.hidden = true;
    this.layer.dataset.testid = 'catch-layer';
    this.layer.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.onTap?.();
    });
  }

  get layerNode(): HTMLElement {
    return this.layer;
  }

  open(chancePct: number, onTap: () => void): void {
    this.chanceNum.textContent = `${Math.round(chancePct)}%`;
    this.chip.textContent = '';
    this.chip.className = 'cr-chip';
    this.onTap = onTap;
    this.root.hidden = false;
    this.layer.hidden = false;
    this.root.classList.remove('graded', 'hot');
    this.label.textContent = 'WAIT';
    this.hot = false;
  }

  /** k: 1 at window open, 0 at arrival. */
  set(x: number, y: number, k: number): void {
    this.root.style.transform = `translate(${x}px, ${y}px)`;
    this.outer.style.transform = `translate(-50%, -50%) scale(${0.45 + 0.9 * Math.max(0, k)})`;
  }

  /** True once a tap would count (Good or Perfect). */
  setHot(on: boolean): void {
    if (on === this.hot || this.root.classList.contains('graded')) return;
    this.hot = on;
    this.root.classList.toggle('hot', on);
    this.label.textContent = on ? 'TAP!' : 'WAIT';
  }

  /** Shows the grade, the new catch chance and what the tap changed (+9, -12). */
  grade(text: string, chancePct: number, delta: number): void {
    this.label.textContent = text;
    this.root.classList.add('graded');
    this.root.classList.remove('hot');
    this.chanceNum.textContent = `${Math.round(chancePct)}%`;
    const d = Math.round(delta);
    this.chip.textContent = d === 0 ? '' : `${d > 0 ? '+' : ''}${d}`;
    this.chip.className = `cr-chip ${d >= 0 ? 'up' : 'down'}`;
    this.root.classList.toggle('missed', d < 0);
    this.onTap = null;
    this.layer.hidden = true;
  }

  close(): void {
    this.root.hidden = true;
    this.layer.hidden = true;
    this.onTap = null;
    this.root.classList.remove('graded', 'hot', 'missed');
  }
}

/** Live yards gained, on a plate just below the ball carrier (pop words go above him). */
export class YardCounter {
  readonly root = el('div', 'yardtick');
  private last = Number.NaN;

  constructor() {
    this.root.hidden = true;
  }

  private on = false;

  /** It appears on the first set(), so a plate from the last play never flashes up. */
  show(on: boolean): void {
    this.on = on;
    if (!on) {
      this.root.hidden = true;
      this.last = Number.NaN;
    }
  }

  set(x: number, y: number, yards: number, firstDown: boolean): void {
    if (!this.on) return;
    this.root.hidden = false;
    this.root.style.transform = `translate(${x}px, ${y}px) translateX(-50%)`;
    this.root.classList.toggle('first', firstDown);
    if (yards === this.last) return;
    this.last = yards;
    this.root.textContent = `${yards > 0 ? '+' : ''}${yards}`;
  }
}

export interface RunInput {
  stickX: number;
  stickY: number;
  sprint: boolean;
}

/**
 * Ball carrier controls (GDD 6.6, reference 05): drag on the left half to steer, swipe on the right half
 * (left/right juke, up truck, down spin), hold SPRINT. The stick always shows at the bottom left while running,
 * so it is plain that the carrier can be steered; a touch anywhere on the left half moves it under the thumb.
 * Keyboard works on desktop.
 */
export class RunControls {
  readonly root = el('div', 'runctl');
  private readonly left = el('div', 'run-left');
  private readonly right = el('div', 'run-right');
  private readonly stickBase = el('div', 'stick-base');
  private readonly stickKnob = el('div', 'stick-knob');
  private readonly sprintBtn = el('button', 'sprint-btn', 'SPRINT');
  private readonly stamina = el('div', 'stamina');
  private readonly staminaFill = el('div', 'stamina-fill');
  private readonly hints = el('div', 'swipe-hints');
  private readonly steerHint = el('div', 'steer-hint', 'DRAG TO STEER');
  private stickId = -1;
  private ox = 0;
  private oy = 0;
  private sprintIds = new Set<number>();
  private swipe = new Map<number, { x: number; y: number; t: number }>();
  private readonly keys = new Set<string>();
  readonly input: RunInput = { stickX: 0, stickY: 0, sprint: false };
  private pendingMove: RunMove | null = null;
  private readonly onKeyDown: (e: KeyboardEvent) => void;
  private readonly onKeyUp: (e: KeyboardEvent) => void;

  constructor(t: Tunables) {
    const arrows = el('div', 'stick-arrows');
    arrows.append(el('span', '', '\u2039'), el('span', '', '\u203a'));
    this.stickBase.append(arrows, this.stickKnob);
    this.stickBase.dataset.testid = 'stick';
    this.restStick();
    this.stamina.append(this.staminaFill);
    this.sprintBtn.dataset.testid = 'sprint';
    this.hints.innerHTML =
      '<div><b>SWIPE</b> ⟷ JUKE</div><div><b>SWIPE</b> ↑ TRUCK</div><div><b>SWIPE</b> ↓ SPIN</div>';
    this.left.append(this.steerHint, this.stickBase);
    this.right.append(this.hints, this.sprintBtn, this.stamina);
    this.root.append(this.left, this.right);
    this.root.hidden = true;

    const r = t.controls.stickRadiusPx;
    this.left.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.left.setPointerCapture?.(e.pointerId);
      this.stickId = e.pointerId;
      const box = this.left.getBoundingClientRect();
      this.ox = e.clientX;
      this.oy = e.clientY;
      this.stickBase.classList.remove('rest');
      this.stickBase.style.left = `${e.clientX - box.left}px`;
      this.stickBase.style.top = `${e.clientY - box.top}px`;
      this.stickKnob.style.transform = 'translate(-50%, -50%)';
    });
    this.left.addEventListener('pointermove', (e) => {
      if (e.pointerId !== this.stickId) return;
      let dx = e.clientX - this.ox;
      let dy = e.clientY - this.oy;
      const len = Math.hypot(dx, dy);
      if (len > r) {
        dx = (dx / len) * r;
        dy = (dy / len) * r;
      }
      this.input.stickX = dx / r;
      this.input.stickY = -dy / r;
      this.stickKnob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
    });
    const endStick = (e: PointerEvent) => {
      if (e.pointerId !== this.stickId) return;
      this.stickId = -1;
      this.input.stickX = 0;
      this.input.stickY = 0;
      this.restStick();
    };
    this.left.addEventListener('pointerup', endStick);
    this.left.addEventListener('pointercancel', endStick);

    this.sprintBtn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.sprintBtn.setPointerCapture?.(e.pointerId);
      this.sprintIds.add(e.pointerId);
      this.sprintBtn.classList.add('on');
    });
    const endSprint = (e: PointerEvent) => {
      this.sprintIds.delete(e.pointerId);
      if (this.sprintIds.size === 0) this.sprintBtn.classList.remove('on');
    };
    this.sprintBtn.addEventListener('pointerup', endSprint);
    this.sprintBtn.addEventListener('pointercancel', endSprint);

    this.right.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.swipe.set(e.pointerId, { x: e.clientX, y: e.clientY, t: performance.now() });
    });
    const endSwipe = (e: PointerEvent) => {
      const s = this.swipe.get(e.pointerId);
      this.swipe.delete(e.pointerId);
      if (!s) return;
      const dx = e.clientX - s.x;
      const dy = e.clientY - s.y;
      const dt = (performance.now() - s.t) / 1000;
      if (dt > t.controls.swipeMaxSeconds || Math.hypot(dx, dy) < t.controls.swipeMinPx) return;
      if (Math.abs(dx) > Math.abs(dy)) this.pendingMove = dx < 0 ? 'jukeLeft' : 'jukeRight';
      else this.pendingMove = dy < 0 ? 'truck' : 'spin';
    };
    this.right.addEventListener('pointerup', endSwipe);
    this.right.addEventListener('pointercancel', (e) => this.swipe.delete(e.pointerId));

    this.onKeyDown = (e) => {
      if (this.root.hidden) return;
      this.keys.add(e.key.toLowerCase());
      const k = e.key.toLowerCase();
      if (k === 'a') this.pendingMove = 'jukeLeft';
      if (k === 'd') this.pendingMove = 'jukeRight';
      if (k === 'w') this.pendingMove = 'truck';
      if (k === 's') this.pendingMove = 'spin';
    };
    this.onKeyUp = (e) => this.keys.delete(e.key.toLowerCase());
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
  }

  show(on: boolean): void {
    this.root.hidden = !on;
    if (!on) {
      this.stickId = -1;
      this.input.stickX = 0;
      this.input.stickY = 0;
      this.restStick();
      this.sprintIds.clear();
      this.sprintBtn.classList.remove('on');
      this.pendingMove = null;
    }
  }

  /** Back to its place at the bottom left, idle. */
  private restStick(): void {
    this.stickBase.classList.add('rest');
    this.stickBase.style.left = '';
    this.stickBase.style.top = '';
    this.stickKnob.style.transform = 'translate(-50%, -50%)';
  }

  /** Reads input for this frame (keyboard adds to touch). */
  read(): RunInput {
    let kx = 0;
    let ky = 0;
    if (this.keys.has('arrowleft')) kx -= 1;
    if (this.keys.has('arrowright')) kx += 1;
    if (this.keys.has('arrowup')) ky += 1;
    if (this.keys.has('arrowdown')) ky -= 1;
    const out = this.input;
    if (kx || ky) {
      out.stickX = kx;
      out.stickY = ky || (kx ? 0.6 : 0);
    } else if (this.stickId < 0) {
      out.stickX = 0;
      out.stickY = 0;
    }
    out.sprint = this.sprintIds.size > 0 || this.keys.has('shift');
    return out;
  }

  takeMove(): RunMove | null {
    const m = this.pendingMove;
    this.pendingMove = null;
    return m;
  }

  setStamina(pct: number): void {
    this.staminaFill.style.width = `${Math.max(0, Math.min(100, pct))}%`;
  }

  showHints(on: boolean): void {
    this.hints.hidden = !on;
    this.steerHint.hidden = !on;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
  }
}

/** Big floating words on a plate at a screen point: PERFECT!, JUKE!, BROKE IT!, FIRST DOWN! */
export class Pops {
  readonly root = el('div', 'pops');

  pop(text: string, x: number, y: number, kind: 'good' | 'bad' | 'plain' = 'good', big = false): void {
    const n = el('div', `pop pop-${kind}${big ? ' pop-big' : ''}`, text);
    n.style.left = `${x}px`;
    n.style.top = `${y}px`;
    this.root.appendChild(n);
    setTimeout(() => n.remove(), 1100);
  }
}
